import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Search, FileText, ChevronRight, ChevronLeft, Filter, X, RefreshCw, Box, ArrowUpDown, ChevronsLeft, ChevronsRight, Type, Minus } from 'lucide-react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import _ from 'lodash'; 
import toast from 'react-hot-toast';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const JPList = ({ onSelect }) => {
    const { t, i18n } = useTranslation();
    // 🎯 كشف الاتجاه بناءً على اللغة (عربي وأوردو)
    const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { canCreate } = usePermissions('jp');
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    // --- State الأساسي ---
    const [jps, setJps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [showAllSites, setShowAllSites] = useState(false);

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';

    // --- قراءة الباراميترز من URL لضمان الـ Persistence ---
    const page = parseInt(searchParams.get('page')) || 1;
    const limit = 50;
    const q = searchParams.get('q') || "";
    const status = searchParams.get('status') || "";
    const site = searchParams.get('site_id') || "";
    const sortBy = searchParams.get('sortBy') || "jpnum";
    const sortDir = searchParams.get('sortDir') || "desc";

    const fetchJPs = useCallback(async (isAll = showAllSites) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const effectiveSite = !isAll && !site ? sessionSite : site;

            const response = await axios.get('/api/jobplans', {
                params: { 
                    page, limit, q, status, 
                    site_id: effectiveSite, 
                    all_sites: Boolean(isAll),
                    sortBy: sortBy, sortDir: sortDir
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setJps(response.data.data);
            setTotalCount(response.data.total);
        } catch (err) {
            console.error("Fetch error:", err);
            toast.error(t('jp.list.fetch_error', 'Failed to load Job Plans'));
        } finally {
            setLoading(false);
        }
    }, [page, q, status, site, sortBy, sortDir, showAllSites, sessionSite, t]); 

    useEffect(() => {
        const timer = setTimeout(() => fetchJPs(), 100); 
        return () => clearTimeout(timer);
    }, [page, q, status, site, sortBy, sortDir, showAllSites, fetchJPs]);

    const updateFilters = (newFilters) => {
        const params = Object.fromEntries(searchParams.entries());
        const updated = { ...params, ...newFilters };
        Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
        setSearchParams(updated);
    };

    const handleSort = (column) => {
        const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
        updateFilters({ sortBy: column, sortDir: direction });
    };

    // 🎯 الدالة المعدلة عشان تباصي الكويري للديتيلز صح
    const handleRowClick = (jpnum) => {
        // 1. تجميع كل الفلاتر النشطة (بما فيها السايت الفعال حالياً)
        const activeFilters = {
            page: page,
            limit: limit,
            q: q,
            status: status,
            sortBy: sortBy,
            sortDir: sortDir,
            all_sites: showAllSites,
            // السر هنا: لو اليوزر مش كاتب سايت، ومش مختار All Sites، نبعت سايت الجلسة عشان الكويري مايتوهش
            site_id: site || (showAllSites ? '' : sessionSite)
        };

        // 2. تنظيف الأوبجيكت من أي قيم فاضية عشان الـ URL يبقى نظيف
        const cleanFilters = Object.fromEntries(
            Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        );

        // 3. تحويل الفلاتر النظيفة لـ Query String
        const queryParams = new URLSearchParams(cleanFilters).toString();

        // 4. التوجيه لشاشة التفاصيل مع دمج الكويري
        navigate(`/jp/${jpnum}${queryParams ? '?' + queryParams : ''}`);
    };

    useEffect(() => {
        const openAll = () => { setShowAllSites(true); updateFilters({page: 1}); };
        const viewMine = () => { setShowAllSites(false); updateFilters({page: 1}); };
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAll);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMine);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAll);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMine);
        };
    }, [searchParams]);

    const totalPages = Math.ceil(totalCount / limit) || 1;

    return (
        <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
            
            {/* Header Toolbar */}
            <div className="bg-white p-3 border-b shadow-sm z-20">
                <div className="flex justify-between items-center px-4">
                    <h2 className="text-[#1a3353] font-black italic text-lg flex items-center gap-2">
                        <FileText size={22} className="text-blue-600" /> 
                        {t('jp.list.title', 'Job Plans Directory')}
                        <RefreshCw 
                            size={18} 
                            className={`cursor-pointer transition-all ${loading ? 'animate-spin text-orange-500' : 'text-blue-400'} ${isRTL ? 'mr-2' : 'ml-2'}`} 
                            onClick={() => fetchJPs()} 
                        />
                    </h2>
                    
                    <div className="flex gap-3">
                        <button onClick={() => setSearchParams({})} className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-600 font-bold transition-colors">
                            <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
                        </button>
                        {canCreate && (
                            <button onClick={() => navigate('/jp/new')} className="bg-[#1a3353] text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-blue-900 shadow-lg transition-all">
                                <Plus size={16} /> {t('jp.list.new_plan', 'NEW PLAN')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-[#1a3353] text-white font-bold uppercase sticky top-0 z-10">
                            <tr>
                                <SortableHeader label={t('jp.fields.jpnum', 'Plan #')} sortKey="jpnum" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('jp.fields.description', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                                <th className="p-3 border-x border-white/10 text-center">{t('jp.fields.status', 'Status')}</th>
                                <SortableHeader label={t('jp.fields.revision', 'Revision')} sortKey="revision" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                                <th className="p-3 text-center border-x border-white/10">{t('jp.fields.site', 'Site')}</th>
                            </tr>
                            {/* Filter Row */}
                            <tr className="bg-[#f8fafc]">
                                <td className="p-1 border-x border-gray-200">
                                    <input 
                                        className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white" 
                                        placeholder={t('jp.list.filter_jp', 'Filter JP...')}
                                        value={searchParams.get('jpnum') || ""} 
                                        onChange={e => updateFilters({ jpnum: e.target.value })} 
                                    />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <input 
                                        className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white" 
                                        placeholder={t('jp.list.search_desc', 'Search description...')}
                                        value={q} 
                                        onChange={e => updateFilters({ q: e.target.value })} 
                                    />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <select 
                                        className="w-full p-1.5 text-blue-900 font-black outline-none border border-gray-100 bg-white" 
                                        value={status} 
                                        onChange={e => updateFilters({ status: e.target.value })}
                                    >
                                        <option value="">{t('common.all', 'ALL')}</option>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="DRAFT">DRAFT</option>
                                        <option value="REVISED">REVISED</option>
                                    </select>
                                </td>
                                <td className="p-1 border-x border-gray-200 bg-gray-100/30"></td>
                                <td className="p-1 border-x border-gray-200">
                                    <input 
                                        className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white text-center" 
                                        placeholder={t('jp.list.filter_site', 'Site...')}
                                        value={site} 
                                        onChange={e => updateFilters({ site_id: e.target.value })} 
                                    />
                                </td>
                            </tr>
                        </thead>
                        
                        <tbody className="divide-y divide-gray-100 italic font-bold">
                            {loading ? (
                                <tr><td colSpan="5" className="p-20 text-center font-black text-gray-300 animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Plans...')}</td></tr>
                            ) : jps.length > 0 ? (
                                jps.map((jp, i) => (
                                    <tr key={i} className="hover:bg-blue-50 cursor-pointer transition-colors group" onClick={() => handleRowClick(jp.jpnum)}>
                                        <td className="p-3 border-x border-gray-50 font-black text-blue-900 group-hover:underline">{jp.jpnum}</td>
                                        <td className="p-3 border-x border-gray-50 text-gray-600">{jp.description}</td>
                                        <td className="p-3 border-x border-gray-50 text-center">
                                            <span className={`px-3 py-1 rounded-full font-black text-[9px] shadow-sm border ${
                                                jp.status === 'ACTIVE' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {jp.status}
                                            </span>
                                        </td>
                                        <td className="p-3 border-x border-gray-50 text-center text-slate-500">{jp.revision}</td>
                                        <td className="p-3 border-x border-gray-50 text-center text-gray-400 font-black">{jp.site_id}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="p-20 text-center font-black text-gray-400 italic uppercase">{t('jp.list.no_records', 'No Job Plans found')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination & Font Footer */}
            <div className={`bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Box size={14} className="text-blue-400"/>
                        <span>{t('common.showing', 'Showing')}: <span className="text-blue-300">{(page-1)*limit+1}-{Math.min(page*limit, totalCount)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                        <Filter size={14} className="text-orange-400"/>
                        <span>{t('common.total', 'Total')}: <span className="text-orange-300">{totalCount}</span></span>
                    </div>
                </div>

                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {/* 🎯 عكسنا الأسهم هنا في حالة الـ RTL */}
                        <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-orange-400 disabled:opacity-20">
                            <ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                        </button>
                        <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-orange-400 disabled:opacity-20">
                            <ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                        </button>
                        <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1">
                            <span className="text-orange-400 text-sm">{page}</span> / {totalPages}
                        </div>
                        <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-orange-400 disabled:opacity-20">
                            <ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                        </button>
                        <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-orange-400 disabled:opacity-20">
                            <ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- المكونات المساعدة ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
    <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-blue-800 transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
        <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
            {label}
            <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-orange-400' : 'text-white/30'} />
        </div>
    </th>
);

export default JPList;